import { useEffect, useState } from 'react';

type ActiveSection = 'journal' | 'masterclass' | 'training';

interface EducationHeroProps {
  setActiveSection: (section: ActiveSection) => void;
}

const EducationHero = ({ setActiveSection }: EducationHeroProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 120);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems: { label: string; id: string; section: ActiveSection }[] = [
    { label: 'Inside Journal', id: 'insights-journal', section: 'journal' },
    { label: 'Stability Masterclass', id: 'insights-journal', section: 'masterclass' },
    { label: 'Method Training Program', id: 'insights-journal', section: 'training' },
  ];

  const handleNavClick = (item: { id: string; section: ActiveSection }) => {
    setActiveSection(item.section);
    setTimeout(() => scrollTo(item.id), 50);
  };

  return (
    <section className="relative overflow-hidden bg-[#faf8f5]" style={{ minHeight: '80vh' }}>

      {/* Background texture */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-1/2 h-full"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/87088fb90bd196cade0e18adccabeeb7.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-white/75" />
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#faf8f5] to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 w-px h-2/3 bg-gradient-to-t from-[#c9a87c]/20 to-transparent" style={{ left: '22%' }} />
        <div className="absolute top-0 right-0 w-px h-2/3 bg-gradient-to-b from-[#c9a87c]/20 to-transparent" style={{ right: '22%' }} />
      </div>

      {/* Top label bar */}
      <div
        className="relative z-10 flex items-center justify-between px-14 pt-7 pb-0"
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.7s ease 0.1s',
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-[1px] bg-[#c9a87c]/50" />
          <span className="text-[#c9a87c] text-[8px] font-sans font-semibold tracking-[0.5em] uppercase">
            Vionne Education
          </span>
        </div>
        <div className="flex items-center gap-8">
          {navItems.map((item, i) => (
            <button
              key={i}
              onClick={() => handleNavClick(item)}
              className="text-[9px] font-sans font-light tracking-[0.25em] uppercase text-[#8a7560]/60 hover:text-[#5c4a35] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main hero body */}
      <div
        className="relative z-10 flex items-end w-full px-14"
        style={{
          minHeight: 'calc(80vh - 60px)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
        }}
      >
        {/* LEFT — large title block */}
        <div
          className="flex flex-col justify-end pb-12 flex-1"
          style={{ paddingLeft: 'max(0px, calc((100vw - 72rem) / 2 + 3rem - 3.5rem))' }}
        >
          <div className="flex items-center gap-4 mb-5">
            <span className="text-[#c9a87c]/40 font-display font-light text-[10px] tracking-[0.4em]">01</span>
            <div className="flex-1 h-[1px] bg-[#c9a87c]/20" />
          </div>

          <h1
            className="font-display font-light text-[#2a2118] leading-[1.0] mb-6"
            style={{ fontSize: 'clamp(2.1rem, 4vw, 4.2rem)', letterSpacing: '-0.01em' }}
          >
            <span className="block">The</span>
            <span className="block italic text-[#c9a87c]">Intelligence</span>
            <span className="block">Behind&nbsp;Stability</span>
            <span className="block italic"></span>
          </h1>

          <p
            className="text-[#3d2e1e] text-[11px] font-sans font-light leading-[1.9] mb-2 max-w-[360px]"
            style={{ letterSpacing: '0.02em' }}
          >
            Education at Vionne is not the transfer of information —
            it is the cultivation of a new way of seeing.
          </p>
          <p className="text-[#4a3828] text-[10.5px] font-sans font-light italic leading-relaxed mb-8 max-w-[320px]">
            A space for practitioners and individuals who seek depth, precision, and lasting change.
          </p>

          <div className="flex items-center gap-6">
            <button
              onClick={() => handleNavClick({ id: 'insights-journal', section: 'training' })}
              className="inline-flex items-center gap-2.5 px-7 py-2.5 bg-[#2a2118] text-[#f5ede3] rounded-full text-[9px] font-sans font-semibold tracking-[0.35em] uppercase hover:bg-[#3d3020] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span>Explore Programs</span>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-[11px]" />
              </div>
            </button>
            <button
              onClick={() => handleNavClick({ id: 'insights-journal', section: 'journal' })}
              className="inline-flex items-center gap-1.5 text-[#8a7560] text-[9px] font-sans font-light tracking-[0.3em] uppercase hover:text-[#2a2118] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span>Inside Journal</span>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-line text-[10px]" />
              </div>
            </button>
          </div>
        </div>

        {/* RIGHT — vertical info column */}
        <div className="flex flex-col justify-end pb-12 w-[28%] shrink-0 pl-10">

          {/* Stat block */}
          <div className="mb-8 pl-7 border-l border-[#c9a87c]/25">
            <div className="mb-5">
              <div className="text-[#2a2118] font-display font-light text-[2.2rem] leading-none mb-0.5" style={{ letterSpacing: '-0.02em' }}>
                3
              </div>
              <div className="text-[#5c4a35] text-[9px] font-sans font-light tracking-[0.3em] uppercase">
                Core Pathways
              </div>
            </div>
            <div className="mb-5">
              <div className="text-[#2a2118] font-display font-light text-[2.2rem] leading-none mb-0.5" style={{ letterSpacing: '-0.02em' }}>
                12+
              </div>
              <div className="text-[#5c4a35] text-[9px] font-sans font-light tracking-[0.3em] uppercase">
                Modules Available
              </div>
            </div>
            <div>
              <div className="text-[#2a2118] font-display font-light text-[2.2rem] leading-none mb-0.5" style={{ letterSpacing: '-0.02em' }}>
                ∞
              </div>
              <div className="text-[#5c4a35] text-[9px] font-sans font-light tracking-[0.3em] uppercase">
                Depth of Practice
              </div>
            </div>
          </div>

          {/* Vertical nav links */}
          <div className="space-y-0">
            {navItems.map((item, i) => (
              <button
                key={i}
                onClick={() => handleNavClick(item)}
                className="group flex items-center justify-between w-full py-3 border-t border-[#c9a87c]/20 text-[#5c4a35]/80 hover:text-[#3d2e1e] transition-all duration-300 cursor-pointer"
              >
                <span className="text-[10px] font-sans font-light tracking-[0.2em] uppercase text-left">
                  {item.label}
                </span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-[10px] text-[#c9a87c]/50 group-hover:text-[#c9a87c] group-hover:translate-x-0.5 transition-all duration-300" />
                </div>
              </button>
            ))}
            <div className="border-t border-[#c9a87c]/20" />
          </div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        style={{
          opacity: visible ? 0.5 : 0,
          transition: 'opacity 1s ease 1s',
        }}
      >
        <div className="w-[1px] h-10 bg-gradient-to-b from-[#c9a87c]/60 to-transparent animate-pulse" />
        <span className="text-[8px] font-sans font-light tracking-[0.4em] uppercase text-[#8a7560]/60">Scroll</span>
      </div>
    </section>
  );
};

export default EducationHero;
